/**
 * Type-safe event emitter for Nova SDK lifecycle events.
 *
 * Used internally to bridge widget state changes to the host application,
 * and exposed publicly via Nova.on() / Nova.off() static methods.
 */

type EventMap = {
  'ticket:resolved': { ticketId: string; summary: string };
  'ticket:updated': { ticketId: string; summary: string; status: string };
  'updates:available': { count: number };
  'widget:opened': Record<string, never>;
  'widget:closed': Record<string, never>;
};

export class NovaEventEmitter {
  private listeners = new Map<string, Set<(...args: unknown[]) => void>>();

  on<K extends keyof EventMap>(event: K, callback: (data: EventMap[K]) => void): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback as (...args: unknown[]) => void);
  }

  off<K extends keyof EventMap>(event: K, callback: (data: EventMap[K]) => void): void {
    this.listeners.get(event)?.delete(callback as (...args: unknown[]) => void);
  }

  emit<K extends keyof EventMap>(event: K, data: EventMap[K]): void {
    this.listeners.get(event)?.forEach((cb) => cb(data));
  }

  removeAllListeners(): void {
    this.listeners.clear();
  }
}

export type { EventMap };
